import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Text, TextField, Select, Button, type SelectOption } from '@chatsdet/ui';
import type { ApiSequenceStep, ApiCatalogListItem } from '@chatsdet/types';
import { DotsSixVertical, Trash, CaretDown, CaretUp, Plus } from '@phosphor-icons/react';
import { ConfigField } from './ConfigField';
import { MacroRichTextField } from './MacroRichTextField';
import { MacroJsonEditor } from './json-editor';
import { StepCardShell, StepCardHeader } from './StepCard';
import { StepValidationsEditor } from './StepValidationsEditor';
import type { CredentialAndPersonaOptions } from './CredentialPersonaPicker';
import { StepAuthenticationField } from './StepAuthenticationField';
import { FormPartsEditor } from './FormPartsEditor';

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;
const METHOD_OPTIONS: SelectOption[] = HTTP_METHODS.map((m) => ({ id: m, label: m }));
const BODY_TYPE_OPTIONS: SelectOption[] = [
  { id: 'raw', label: 'Raw JSON' },
  { id: 'form', label: 'Form data (multipart)' },
];

/**
 * Builds the subtext shown under an endpoint's `method pattern` label so
 * endpoints from different properties (or bare normalized patterns like
 * `GET /:token/:token/:uuid`) stay distinguishable in the picker. The domain
 * is the discriminator that was missing, so it's always included when known;
 * the human-readable name is layered in front of it when present.
 */
function apiRequestDescription(req: ApiCatalogListItem): string | undefined {
  if (req.name && req.domain) return `${req.name} · ${req.domain}`;
  return req.name || req.domain || undefined;
}

/** Matches label or description, case-insensitive — mirrors Select's defaultFilter. */
function filterApiRequestOption(item: SelectOption, query: string): boolean {
  const q = query.toLowerCase();
  return (
    item.label.toLowerCase().includes(q) || Boolean(item.description?.toLowerCase().includes(q))
  );
}

type SortableStepCardProps = {
  sortableId: string;
  step: ApiSequenceStep;
  index: number;
  apiRequests: ApiCatalogListItem[];
  /** Derived once by ApiSequenceEditor and shared by all step cards. */
  credentialPickerOptions: CredentialAndPersonaOptions;
  onUpdate: (step: ApiSequenceStep) => void;
  onRemove: () => void;
  isAdvancedOpen: boolean;
  onToggleAdvanced: () => void;
  /** Set when this step is Custom and its URL template is empty (mirrors STEP_CUSTOM_URL_REQUIRED). */
  urlTemplateError?: string;
  availableVariables: string[];
  workflowVariables?: Array<{ name: string; source: string }>;
};

export function SortableStepCard({
  sortableId,
  step,
  index,
  apiRequests,
  credentialPickerOptions,
  onUpdate,
  onRemove,
  isAdvancedOpen,
  onToggleAdvanced,
  urlTemplateError,
  availableVariables,
  workflowVariables,
}: SortableStepCardProps) {
  const displayStepId = step.stepId || `step${index + 1}`;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: sortableId,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const apiRequestOptions: SelectOption[] = React.useMemo(() => {
    return apiRequests.map((req) => ({
      id: req.id,
      label: `${req.method} ${req.pattern}`,
      description: apiRequestDescription(req),
    }));
  }, [apiRequests]);

  const selectedRequest = React.useMemo(() => {
    return apiRequests.find((r) => r.id === step.apiRequestId);
  }, [apiRequests, step.apiRequestId]);

  const hasAdvancedSettings =
    step.method ||
    step.urlTemplate ||
    step.bodyTemplate ||
    (step.formParts && step.formParts.length > 0) ||
    (step.queryParams && step.queryParams.length > 0) ||
    (step.headersTemplate && Object.keys(step.headersTemplate).length > 0) ||
    (step.expectedStatusCodes && step.expectedStatusCodes.length > 0) ||
    (step.validations && step.validations.length > 0);

  return (
    <StepCardShell>
      <div ref={setNodeRef} style={style}>
        {/* Header with drag handle */}
        <div className="cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
          <StepCardHeader
            stepNumber={index + 1}
            stepId={step.stepId}
            left={<DotsSixVertical size={18} className="text-muted-foreground" weight="bold" />}
            right={
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Remove step"
                onPress={onRemove}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash size={16} />
              </Button>
            }
          />
        </div>

        {/* Content */}
        <div className="p-4 space-y-5">
          {/* Endpoint Section */}
          <ConfigField
            label="Endpoint"
            description="Select which API endpoint this step will call, or choose Custom to define your own below."
            required
          >
            <Select
              aria-label="API Endpoint"
              items={[
                {
                  id: '',
                  label: 'Custom',
                  description: 'Define the URL and method yourself below.',
                },
                ...apiRequestOptions,
              ]}
              selectedKey={step.apiRequestId || ''}
              onSelectionChange={(key) => {
                const req = apiRequests.find((r) => r.id === key);
                onUpdate({
                  ...step,
                  apiRequestId: String(key),
                  method: req?.method || step.method,
                  urlTemplate: req?.exampleUrl || step.urlTemplate,
                });
              }}
              filterFn={filterApiRequestOption}
            />
          </ConfigField>

          <StepAuthenticationField
            step={step}
            credentialPickerOptions={credentialPickerOptions}
            onUpdate={onUpdate}
          />

          {/* Step ID */}
          <ConfigField
            label="Step ID"
            description={`Unique name for this step. Later steps reference its data with {{${displayStepId}.response.path}}`}
          >
            <TextField
              aria-label="Step ID"
              value={step.stepId || ''}
              onChange={(value) => onUpdate({ ...step, stepId: value || undefined })}
              placeholder={`step${index + 1}`}
            />
          </ConfigField>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Advanced Settings Toggle */}
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full"
            onClick={onToggleAdvanced}
          >
            {isAdvancedOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
            Request Configuration
            {urlTemplateError && !isAdvancedOpen ? (
              <span className="text-xs text-destructive ml-1">(URL required)</span>
            ) : (
              hasAdvancedSettings &&
              !isAdvancedOpen && <span className="text-xs text-primary ml-1">(configured)</span>
            )}
          </button>

          {/* Advanced Settings Content */}
          {isAdvancedOpen && (
            <div className="space-y-5 pl-0">
              {/* Method Override */}
              <ConfigField
                label="Method Override"
                description="Override the HTTP method if you need something different from the endpoint's default."
              >
                <Select
                  aria-label="Method"
                  items={[
                    {
                      id: '',
                      label: selectedRequest?.method
                        ? `Default (${selectedRequest.method})`
                        : 'Default',
                    },
                    ...METHOD_OPTIONS,
                  ]}
                  selectedKey={step.method || ''}
                  onSelectionChange={(key) =>
                    onUpdate({ ...step, method: key === '' ? undefined : String(key) })
                  }
                />
              </ConfigField>

              {/* URL Template */}
              <MacroRichTextField
                label="URL"
                description="The full URL for this request. Use $MACRO() for dynamic values or {{stepId.response.path}} to insert values from previous steps."
                aria-label="URL Template"
                value={step.urlTemplate || ''}
                onChange={(value) => onUpdate({ ...step, urlTemplate: value || undefined })}
                placeholder={selectedRequest?.exampleUrl || 'https://api.example.com/...'}
                className="font-mono text-sm"
                errorMessage={urlTemplateError}
              />

              {/* Query Parameters */}
              <ConfigField
                label="Query Parameters"
                description="Key-value pairs appended to the URL. Values support $MACRO() and {{stepId.response.path}} syntax."
              >
                <div className="space-y-1.5">
                  {(step.queryParams ?? []).length > 0 && (
                    <>
                      <div className="grid grid-cols-[1fr_1fr_28px] gap-2 px-1">
                        <Text size="xs" className="text-muted-foreground">
                          Key
                        </Text>
                        <Text size="xs" className="text-muted-foreground">
                          Value
                        </Text>
                        <span />
                      </div>
                      {(step.queryParams ?? []).map((param, pIdx) => (
                        <div
                          key={pIdx}
                          className="grid grid-cols-[1fr_1fr_28px] gap-2 items-center"
                        >
                          <TextField
                            aria-label={`Query param ${pIdx + 1} key`}
                            value={param.key}
                            onChange={(val) => {
                              const next = [...(step.queryParams ?? [])];
                              next[pIdx] = { ...next[pIdx], key: val };
                              onUpdate({ ...step, queryParams: next });
                            }}
                            placeholder="key"
                            className="font-mono"
                          />
                          <MacroRichTextField
                            aria-label={`Query param ${pIdx + 1} value`}
                            value={param.value}
                            onChange={(val) => {
                              const next = [...(step.queryParams ?? [])];
                              next[pIdx] = { ...next[pIdx], value: val };
                              onUpdate({ ...step, queryParams: next });
                            }}
                            placeholder="value"
                            className="font-mono"
                          />
                          <Button
                            variant="icon"
                            iconOnly
                            size="sm"
                            onPress={() => {
                              const next = (step.queryParams ?? []).filter((_, i) => i !== pIdx);
                              onUpdate({
                                ...step,
                                queryParams: next.length > 0 ? next : undefined,
                              });
                            }}
                            tooltip="Remove"
                          >
                            <Trash size={14} />
                          </Button>
                        </div>
                      ))}
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      const next = [...(step.queryParams ?? []), { key: '', value: '' }];
                      onUpdate({ ...step, queryParams: next });
                    }}
                    iconLeft={<Plus size={14} />}
                  >
                    Add
                  </Button>
                </div>
              </ConfigField>

              {/* Request Body — raw JSON and multipart form parts are mutually
                  exclusive (formParts/bodyTemplate can never both be set, per
                  validate-config.ts); the mode selector below enforces that by
                  clearing the other field whenever the mode changes. */}
              <ConfigField
                label="Body Type"
                description="Send a raw JSON body, or a multipart/form-data body built from named parts."
              >
                <Select
                  aria-label="Body Type"
                  items={BODY_TYPE_OPTIONS}
                  selectedKey={step.formParts !== undefined ? 'form' : 'raw'}
                  onSelectionChange={(key) => {
                    if (key === 'form') {
                      if (step.formParts !== undefined) return;
                      onUpdate({ ...step, bodyTemplate: undefined, formParts: [] });
                    } else {
                      if (step.formParts === undefined) return;
                      onUpdate({ ...step, formParts: undefined });
                    }
                  }}
                />
              </ConfigField>

              {step.formParts !== undefined ? (
                <ConfigField
                  label="Form Parts"
                  description="Named parts sent as multipart/form-data. Text values support $MACRO() and {{stepId.response.path}} syntax; file parts are sourced from an attachment."
                >
                  <FormPartsEditor
                    parts={step.formParts}
                    onChange={(parts) => onUpdate({ ...step, formParts: parts })}
                  />
                </ConfigField>
              ) : (
                <MacroJsonEditor
                  label="Request Body"
                  description="JSON body to send with this request. Use $MACRO() for dynamic values, {{step1.response.data.id}} for previous step data, or $var.name for workflow variables."
                  aria-label="Body Template"
                  value={step.bodyTemplate || ''}
                  onChange={(value) => onUpdate({ ...step, bodyTemplate: value || undefined })}
                  placeholder={'{\n  "key": "value"\n}'}
                  formatJson
                  minHeight="200px"
                  maxHeight="400px"
                />
              )}

              {/* Headers */}
              <ConfigField
                label="Headers"
                description="Custom HTTP headers for this request. Values support $MACRO() and {{stepId.response.path}} syntax."
              >
                <div className="space-y-1.5">
                  {(() => {
                    const headerEntries = Object.entries(step.headersTemplate ?? {});
                    return headerEntries.length > 0 ? (
                      <>
                        <div className="grid grid-cols-[1fr_1fr_28px] gap-2 px-1">
                          <Text size="xs" className="text-muted-foreground">
                            Name
                          </Text>
                          <Text size="xs" className="text-muted-foreground">
                            Value
                          </Text>
                          <span />
                        </div>
                        {headerEntries.map(([key, value], hIdx) => (
                          <div
                            key={hIdx}
                            className="grid grid-cols-[1fr_1fr_28px] gap-2 items-center"
                          >
                            <TextField
                              aria-label={`Header ${hIdx + 1} name`}
                              value={key}
                              onChange={(val) => {
                                const entries = Object.entries(step.headersTemplate ?? {});
                                entries[hIdx] = [val, entries[hIdx][1]];
                                const newHeaders = Object.fromEntries(entries.filter(([k]) => k));
                                onUpdate({
                                  ...step,
                                  headersTemplate:
                                    Object.keys(newHeaders).length > 0 ? newHeaders : undefined,
                                });
                              }}
                              placeholder="Header-Name"
                              className="font-mono"
                            />
                            <MacroRichTextField
                              aria-label={`Header ${hIdx + 1} value`}
                              value={value}
                              onChange={(val) => {
                                const entries = Object.entries(step.headersTemplate ?? {});
                                entries[hIdx] = [entries[hIdx][0], val];
                                const newHeaders = Object.fromEntries(entries.filter(([k]) => k));
                                onUpdate({
                                  ...step,
                                  headersTemplate:
                                    Object.keys(newHeaders).length > 0 ? newHeaders : undefined,
                                });
                              }}
                              placeholder="value"
                              className="font-mono"
                            />
                            <Button
                              variant="icon"
                              iconOnly
                              size="sm"
                              onPress={() => {
                                const entries = Object.entries(step.headersTemplate ?? {}).filter(
                                  (_, i) => i !== hIdx
                                );
                                const newHeaders = Object.fromEntries(entries);
                                onUpdate({
                                  ...step,
                                  headersTemplate:
                                    Object.keys(newHeaders).length > 0 ? newHeaders : undefined,
                                });
                              }}
                              tooltip="Remove"
                            >
                              <Trash size={14} />
                            </Button>
                          </div>
                        ))}
                      </>
                    ) : null;
                  })()}
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => {
                      const current = step.headersTemplate ?? {};
                      onUpdate({ ...step, headersTemplate: { ...current, '': '' } });
                    }}
                    iconLeft={<Plus size={14} />}
                  >
                    Add
                  </Button>
                </div>
              </ConfigField>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Response Handling Section Header */}
              <Text size="sm" weight="medium" className="text-muted-foreground">
                Response Handling
              </Text>

              {/* Expected Status Codes */}
              <ConfigField
                label="Expected Status Codes"
                description="Which HTTP statuses count as success. Leave empty to accept any 2xx. Declare a non-2xx code to assert a failure path — e.g. 404 after a delete, or 401 for an unauthenticated call."
              >
                <TextField
                  aria-label="Expected Status Codes"
                  value={step.expectedStatusCodes?.join(', ') || ''}
                  onChange={(value) => {
                    const codes = value
                      .split(',')
                      .map((s) => parseInt(s.trim(), 10))
                      .filter((n) => !isNaN(n) && n >= 100 && n < 600);
                    onUpdate({
                      ...step,
                      expectedStatusCodes: codes.length > 0 ? codes : undefined,
                    });
                  }}
                  placeholder="Any 2xx"
                />
              </ConfigField>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Validations */}
              <ConfigField
                label="Validations"
                description="Assert conditions on response values. The step fails if any validation doesn't pass."
              >
                <StepValidationsEditor
                  validations={step.validations ?? []}
                  onChange={(validations) =>
                    onUpdate({
                      ...step,
                      validations: validations.length > 0 ? validations : undefined,
                    })
                  }
                  availableStepIds={availableVariables}
                />
              </ConfigField>
            </div>
          )}

          {/* Available Variables */}
          {availableVariables.length > 0 && (
            <>
              <div className="border-t border-border" />
              <div className="space-y-2">
                <Text size="xs" weight="medium" className="text-muted-foreground">
                  Available Variables
                </Text>
                <div className="flex flex-wrap gap-1.5">
                  {availableVariables.map((v) => (
                    <code
                      key={v}
                      className="px-2 py-1 rounded bg-muted font-mono text-[11px] text-muted-foreground"
                    >
                      {`{{${v}.response...}}`}
                    </code>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Workflow Variables */}
          {workflowVariables && workflowVariables.length > 0 && (
            <>
              <div className="border-t border-border" />
              <div className="space-y-2">
                <Text size="xs" weight="medium" className="text-muted-foreground">
                  Workflow Variables
                </Text>
                <div className="flex flex-wrap gap-1.5">
                  {workflowVariables.map((v) => (
                    <code
                      key={v.name}
                      className="px-2 py-1 rounded bg-muted font-mono text-[11px] text-muted-foreground"
                    >
                      {`$var.${v.name}`}
                    </code>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </StepCardShell>
  );
}
